<template>
    <card-grid-item
        :tab-widget="tabWidget"
        :card-class="['card-grid']"
        :min-w="3"
        :min-h="minHeight"
        :resized="handleResized"
        @tab-widget-settings-click="handleTabWidgetSettingsClick"
    >
        <template v-slot:card-title>
            {{ title }}
        </template>
        <template v-slot:card-body>
            <busy-modal :show="busy" class="chart-widget-spinner" />
            <vue-apex-charts v-if="ready" height="350" :options="options" :series="series" />
        </template>
        <template v-slot:tab-widget-settings-modal>
            <tab-widget-settings-modal-form
                :modal-id="tabWidgetSettingsModalID"
                :update="updateTabWidgetSettings"
                :initial-values="tabWidgetSettingsInitialValues"
                :with-chart-interval="true"
                :with-daily-chart-interval="true"
                :with-yearly-chart-interval="true"
                :with-chart-period="true"
                @success-update="handleSuccessTabWidgetSettingsUpdate"
            ></tab-widget-settings-modal-form>
        </template>
    </card-grid-item>
</template>

<script>
import API from '../../../../api';
import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
import CardGridItem from '../common/card-grid-item.vue';
import DeparturesCardGrid from '../common/departures-card-grid.vue';
import { DASHBOARD_STAYLIO_INSTALL_BASE_HISTORY_ENDPOINT } from '../../../../endpoints';
import GoBackIconButton from '../../../generic/go-back-icon-button/go-back-icon-button.vue';
import {
    getWidgetGridRowsNumber,
    getApexChartsGradientFill,
    DEFAULT_DASHBOARD_CHART_INTERVAL,
    getDefaultChartPeriodBasedOnInterval,
} from '../helpers';
import ResponsiveButton from '../../../generic/responsive-button/responsive-button.vue';
import TabWidgetSettingsModalForm from '../common/tab-widget-settings-modal-form.vue';
import VueApexCharts from 'vue3-apexcharts';
import cardGridItemBaseMixin from '../common/card-grid-item-base-mixin';

export default {
    mixins: [cardGridItemBaseMixin],
    name: 'StaylioInstallBaseHistoryChartCardGridItem',
    components: {
        BusyModal,
        CardGridItem,
        TabWidgetSettingsModalForm,
        VueApexCharts,
        ResponsiveButton,
        GoBackIconButton,
        DeparturesCardGrid,
    },
    data() {
        return {
            autoWidthExclude: ['guest', 'property_name'],
            busy: null,
            chartData: [],
            selectedDay: null,
            limit: getWidgetGridRowsNumber({
                tabWidget: this.tabWidget,
            }),
            dates: [],
        };
    },
    computed: {
        tabWidgetSettingsInitialValues() {
            return {
                settings: {
                    name: this.tabWidget.settings.name || this.defaultName,
                    chart_period:
                        this.tabWidget.settings.chart_period ||
                        getDefaultChartPeriodBasedOnInterval(DEFAULT_DASHBOARD_CHART_INTERVAL),
                    chart_interval: this.tabWidget.settings.chart_interval || DEFAULT_DASHBOARD_CHART_INTERVAL,
                },
            };
        },
        series() {
            return this.chartData.map((item) => {
                return {
                    name: item.name,
                    data: item.installed_staylio,
                };
            });
        },
        options() {
            return {
                chart: {
                    type: 'area',
                    toolbar: {
                        show: false,
                    },
                    zoom: {
                        enabled: false,
                    },
                },
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    curve: 'straight',
                },
                fill: getApexChartsGradientFill(),
                grid: {
                    xaxis: {
                        lines: {
                            show: true,
                        },
                    },
                },
                xaxis: {
                    type: 'datetime',
                    categories: this.dates,
                    labels: {
                        datetimeFormatter: {
                            month: 'MMM',
                            day: 'MMM dd',
                        },
                    },
                },
                yaxis: {
                    labels: {
                        formatter: (val) => `${val}`,
                    },
                },
                tooltip: {
                    x: {
                        format: 'dd MMM, yyyy',
                    },
                    y: {
                        formatter: (val) => `${val} Users`,
                    },
                },
            };
        },
    },
    mounted() {
        this.fetchChartData();
    },
    methods: {
        async fetchChartData() {
            try {
                this.busy = true;
                const interval = this.tabWidget.settings.chart_interval || DEFAULT_DASHBOARD_CHART_INTERVAL;
                const period = this.tabWidget.settings.chart_period || getDefaultChartPeriodBasedOnInterval(interval);

                const response = await API.get(DASHBOARD_STAYLIO_INSTALL_BASE_HISTORY_ENDPOINT(), {
                    params: {
                        period: period,
                        interval: interval,
                    },
                });

                this.dates = response.data.map((item) => item.date);

                const sources =
                    response.data.map((items) => {
                        const source = items.data.map((item) => {
                            return item.booking_method_case;
                        });
                        return source;
                    })[0] || [];

                const list = [];
                for (let i = 0; i < sources.length; i++) {
                    const source = sources[i];
                    const percentage = response.data.map((items) => {
                        if (!items.data[i]?.installed_staylio) {
                            return 0;
                        }
                        let p = (items.data[i].installed_staylio / items.data[i].total) * 100;
                        if (isNaN(p)) {
                            p = 0;
                        }
                        return p.toFixed(2);
                    });

                    list.push({
                        name: source,
                        percentage: percentage,
                        total: response.data.map((items) => items.data[i]?.total || 0),
                        installed_staylio: response.data.map((items) => items.data[i]?.installed_staylio || 0),
                    });
                }

                this.chartData = list;
            } catch (e) {
                console.log(e);
                this.errorToast({ text: 'Could not fetch chart data' });
            } finally {
                this.busy = false;
            }
        },
        handleResized() {
            this.limit = getWidgetGridRowsNumber({ tabWidget: this.tabWidget });
            this.cardGridKey += 1;
        },
    },
};
</script>
